import React from "react";
import { useQuery } from "react-query";
import { apollo } from "../../../../utils/apollo";
import { toast } from "react-toastify";
import { GET_SHAFAFIYA_HISTORY } from "../../../../utils/graphql";
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  InformationCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import StatusBadge from "../../../../components/StatusBadge";
import Loading from "../../../../components/Loading";
import IconButton from "../../../../components/IconButton";
import { useTranslation } from "react-i18next";
import { cn } from "../../../../utils/style";

// Function to fetch the Shafafiya history
const fetchShafafiyaHistory = async (emiratesId) => {
  const response = await apollo.query({
    query: GET_SHAFAFIYA_HISTORY,
    variables: {
      eid: emiratesId,
    },
    fetchPolicy: "network-only", // Ensure we get fresh data
  });
  return response.data.getShafafiyaHistory;
};
function CocHistoryView({ emiratesId, onClose }) {
  const { t } = useTranslation();
  const {
    data: historyData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery(
    ["shafafiyaHistory", emiratesId],
    () => fetchShafafiyaHistory(emiratesId),
    {
      enabled: !!emiratesId,
      onError: (err) => {
        toast.error(
          err.message ||
            t("common.error_fetching_history", "Error fetching history data"),
        );
      },
    },
  );
  const handleExport = () => {
    if (!historyData || historyData.length === 0) return;
    const headers = [
      "Transaction Date",
      "Status",
      "First Name",
      "Middle Name",
      "Last Name",
      "Birthdate",
      "Policy Start Date",
      "Policy Renewal Date",
      "Policy Expiry Date",
      "Relation",
      "Relation To",
      "Insurance License",
      "Unified Number",
      "Emirates ID",
    ];
    const csvContent = [
      headers.join(","),
      ...historyData.map((row) =>
        [
          row.transactionDate || "",
          row.status || "",
          row.firstName || "",
          row.middleName || "",
          row.lastName || "",
          row.birthdate || "",
          row.policyStartDate || "",
          row.policyRenewalDate || "",
          row.policyExpiryDate || "",
          row.relation || "",
          row.relationTo || "",
          row.insuranceLicense || "",
          row.unifiedNumber || "",
          row.emiratesId || "",
        ]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `shafafiya_history_${emiratesId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <div className="layer-base mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
      {/* Header */}
      <div className="px-6 py-5 flex justify-between items-center border-b border-slate-100 bg-slate-50/30">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg shadow-sm">
            <ClockIcon className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 leading-none mb-1">
              {t("shafafiya.history_title", "Shafafiya History")}
            </h3>
            <p className="text-xs text-slate-500 font-medium">
              {t("shafafiya.eid_results", "Results for EID")}:{" "}
              <span className="text-primary-600 font-mono font-bold tracking-tight">
                {emiratesId}
              </span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isLoading || isFetching}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-bold shadow-sm transition-all duration-300 hover:bg-slate-50 hover:border-slate-300 hover:shadow active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon
              className={cn(
                "h-4 w-4 text-slate-500",
                (isLoading || isFetching) && "animate-spin",
              )}
            />
            {t("common.refresh", "Refresh")}
          </button>
          {historyData && historyData.length > 0 && (
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-bold shadow-sm transition-all duration-300 hover:bg-slate-50 hover:border-slate-300 hover:shadow active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            >
              <ArrowDownTrayIcon className="h-4 w-4 text-slate-500" />
              {t("common.export", "Export")}
            </button>
          )}
          {onClose && (
            <IconButton
              onClick={onClose}
              className="bg-white border-slate-200 hover:bg-red-50 hover:text-red-600 hover:border-red-100 group transition-all duration-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </IconButton>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-1">
        {isLoading ? (
          <div className="py-24 flex flex-col items-center justify-center">
            <Loading
              message={t(
                "shafafiya.fetching_history",
                "Fetching Shafafiya history...",
              )}
            />
          </div>
        ) : error ? (
          <div className="p-8 flex flex-col items-center justify-center text-center">
            <div className="bg-red-50 rounded-2xl p-6 border border-red-100 max-w-md shadow-sm">
              <ExclamationCircleIcon className="h-10 w-10 text-red-500 mx-auto mb-3" />
              <p className="text-sm text-red-700 font-bold leading-relaxed">
                {error.message ||
                  t(
                    "common.error_load_history",
                    "Could not load history. Please try again later.",
                  )}
              </p>
            </div>
          </div>
        ) : historyData && historyData.length > 0 ? (
          <div className="overflow-x-auto rounded-xl border border-slate-100 shadow-inner">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/50">
                <tr>
                  <th className="px-6 py-4 text-start text-[10px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                    {t("shafafiya.transaction_date", "Transaction Date")}
                  </th>
                  <th className="px-6 py-4 text-start text-[10px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                    {t("shafafiya.status", "Status")}
                  </th>
                  <th className="px-6 py-4 text-start text-[10px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                    {t("shafafiya.full_name", "Full Name")}
                  </th>
                  <th className="px-6 py-4 text-start text-[10px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                    {t("shafafiya.birthdate", "Birthdate")}
                  </th>
                  <th className="px-6 py-4 text-start text-[10px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                    {t("shafafiya.policy_range", "Policy Range")}
                  </th>
                  <th className="px-6 py-4 text-start text-[10px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                    {t("shafafiya.relation", "Relation")}
                  </th>
                  <th className="px-6 py-4 text-start text-[10px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                    {t("shafafiya.license", "License")}
                  </th>
                  <th className="px-6 py-4 text-start text-[10px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                    {t("shafafiya.identifiers", "Identifiers")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {historyData.map((history, index) => (
                  <tr
                    key={index}
                    className="hover:bg-slate-50/50 transition-colors group"
                  >
                    <td className="px-6 py-4 text-xs font-bold text-slate-900 font-mono tracking-tight">
                      {history.transactionDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge
                        status={history.status}
                        className="scale-90 origin-left"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs font-bold text-slate-700 leading-relaxed">
                        {`${history.firstName} ${history.middleName ? history.middleName + " " : ""}${history.lastName}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs font-bold text-slate-500 font-mono tracking-tight">
                      {history.birthdate}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter w-10">
                            {t("Start")}:
                          </span>
                          <span className="text-xs font-bold text-slate-600 font-mono tracking-tight">
                            {history.policyStartDate}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter w-10">
                            {t("End")}:
                          </span>
                          <span className="text-xs font-bold text-slate-600 font-mono tracking-tight">
                            {history.policyExpiryDate}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-xs font-black text-primary-600 uppercase tracking-wider">
                          {history.relation}
                        </span>
                        <span className="text-[10px] font-bold text-slate-400">
                          {t("To")}:
                          {history.relationTo}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs font-bold text-slate-600 font-mono tracking-tight">
                      {history.insuranceLicense}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[9px] font-black text-slate-500 tracking-tighter">
                            {t("UID")}
                          </span>
                          <span className="text-xs font-bold text-slate-700 font-mono tracking-tight">
                            {history.unifiedNumber}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                          <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[9px] font-black text-slate-500 tracking-tighter">
                            {t("EID")}
                          </span>
                          <span className="text-[11px] font-bold text-slate-600 font-mono tracking-tight">
                            {history.emiratesId}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-20 flex flex-col items-center justify-center text-center layer-flat mx-4 mb-4 mt-2 border-dashed border-2">
            <div className="p-4 bg-slate-100 rounded-full mb-4">
              <InformationCircleIcon className="h-8 w-8 text-slate-400" />
            </div>
            <h4 className="text-base font-bold text-slate-700 mb-1">
              {t("shafafiya.no_history_found", "No History Found")}
            </h4>
            <p className="text-sm text-slate-400 max-w-xs mx-auto font-medium">
              {t(
                "shafafiya.no_history_description",
                "We couldn't find any historical records associated with this Emirates ID.",
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
export default CocHistoryView;
